import { useGetDatasetDataInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDataApi } from "api/DatasetDataApi";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { DatasetDataLagLeadRequest, UpdateDatasetData } from "api/model/requests";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { booleanTypeTemplate } from "components/templates/DataTableCellTemplates";
import { useDisplayTimezone } from "hooks/useTimezone";
import React, { useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import {
  getDataConfigLocalFullyFilledConfig,
  remakeArrayIntoOtherInterface,
} from "../data-input-output-table/utils";
import { ICombinedData } from "../data-input-output-table/common/api/interfaces/interfaces";
import { DatasetStatusControllerProps } from "./DatasetStatusControllerTypes";

export default function DatasetStatusController(props: DatasetStatusControllerProps) {
  const displayTz = useDisplayTimezone();
  const setStatus = useComponentStateCallback(props);

  const [dataQuery, setDataQuery] = useState<Array<ICombinedData>>([]);
  const config: DatasetDataLagLeadRequest = getDataConfigLocalFullyFilledConfig(props.config || {});

  const { data, isError, isLoading } = useGetDatasetDataInfo([props.identifier], config, {
    refetchInterval: props.config?.updateFrequency ? props.config.updateFrequency * 1000 : undefined,
  });

  useEffect(() => {
    if (!isLoading && data !== undefined) {
      setStatus({ state: "finished" });
    }
  }, [isLoading]);

  if (isError) {
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      severity: "error",
    });
    setStatus({ state: "error", error: T("alert.failed-to-fetch-data") });
  }

  useEffect(() => {
    if (data && !isError && !isLoading && data.length > 0) {
      setDataQuery(remakeArrayIntoOtherInterface(data, [{ identifier: props.identifier }]));
    }
  }, [data, isError, isLoading, props.identifier]);

  const hasIdentifier = props.identifier?.length > 0;
  const hasValues =
    hasIdentifier &&
    data &&
    data.length > 0 &&
    dataQuery.length > 0 &&
    dataQuery[0].data &&
    dataQuery[0].data.length > 0;

  if (!hasIdentifier) {
    return (
      <div className="flex items-center gap-2 text-gray-500 h-full">
        <i className="pi pi-info-circle text-xl" />
        <span>{T("message.no-identifier-selected")}</span>
      </div>
    );
  }

  if (!hasValues) {
    return (
      <div className="flex items-center gap-2 text-gray-500 h-full">
        <i className="pi pi-exclamation-triangle text-xl" />
        <span>{T("tooltip.status-not-found")}</span>
      </div>
    );
  }

  const currentData: InstantDoubleValue[] = dataQuery[0].data;
  const firstKey = currentData[0].key;

  const changeBooleanValue = (val: boolean) => {
    const boolToNumber = val ? 1 : 0;

    DatasetDataApi.updateData([
      {
        identifier: props.identifier,
        values: { [firstKey]: boolToNumber },
      },
    ] as UpdateDatasetData[])
      .then(() => {
        easasAlert({ summary: T("alert.datasets-updated"), severity: "success" });
        setDataQuery(prev =>
          prev.map(entry =>
            entry.identifier === props.identifier
              ? {
                  ...entry,
                  data: [{ ...entry.data[0], value: boolToNumber }],
                }
              : entry
          )
        );
      })
      .catch(er => {
        console.error(er);
        easasAlert({
          summary: T("alert.error-while-updating-datasets"),
          severity: "error",
        });
      });
  };

  return (
    <div className="flex justify-content-center h-full">
      {booleanTypeTemplate(currentData, changeBooleanValue, displayTz)}
    </div>
  );
}
